import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

type Tenant = {
  _id: string;
  slug: string;
  name: string;
  status: string;
  plan: string;
  createdAt?: string;
};

type AdminUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  memberships: { tenantId: string; role: string }[];
};

type Tab = 'tenants' | 'users';

export default function Admin() {
  const user = useAuth((s) => s.user);
  const nav = useNavigate();
  const [tab, setTab] = useState<Tab>('tenants');

  useEffect(() => {
    if (user && user.role !== 'global_superadmin') nav('/app', { replace: true });
  }, [user, nav]);

  if (!user) return null;

  return (
    <main className="text-white">
      <div className="max-w-5xl mx-auto px-8 py-10">
        <div className="flex items-center gap-3 mb-6">
          <h1 className="h-display text-3xl">Superadmin</h1>
        </div>
        <div className="flex gap-2 mb-6 border-b border-dgal-navy-2">
          {(['tenants', 'users'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={
                'px-4 py-2 text-sm capitalize border-b-2 transition ' +
                (tab === t
                  ? 'border-dgrp-red text-dgrp-red'
                  : 'border-transparent text-dgal-mist hover:text-white')
              }
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 'tenants' ? <TenantsTab /> : <UsersTab />}
      </div>
    </main>
  );
}

function TenantsTab() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [plan, setPlan] = useState<'founder' | 'standard'>('standard');
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    const { data } = await api.get('/api/admin/tenants');
    setTenants(data.tenants);
  }
  useEffect(() => {
    load();
  }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      await api.post('/api/admin/tenants', { slug, name, plan });
      setSlug('');
      setName('');
      load();
    } catch (e2: unknown) {
      const error = e2 as { response?: { data?: { error?: { message?: string } } } };
      setErr(error?.response?.data?.error?.message || 'Failed');
    }
  }

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div>
        <h2 className="text-lg font-semibold mb-3">Tenants</h2>
        <ul className="bg-dgal-navy/40 rounded border border-dgal-navy-2 divide-y divide-dgal-navy-2">
          {tenants.map((t) => (
            <li key={t._id} className="px-4 py-3 flex items-center justify-between">
              <div>
                <div className="font-medium">{t.name}</div>
                <div className="text-xs text-dgal-mist">
                  {t.slug} · {t.plan} · {t.status}
                </div>
              </div>
            </li>
          ))}
          {tenants.length === 0 && (
            <li className="px-4 py-3 text-sm text-dgal-mist">No tenants yet.</li>
          )}
        </ul>
      </div>

      <form onSubmit={create} className="bg-dgal-navy/40 rounded border border-dgal-navy-2 p-5 space-y-4 h-fit">
        <h2 className="text-lg font-semibold">Create tenant</h2>
        <input
          placeholder="slug (e.g. tdg)"
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase())}
          className="w-full bg-dgal-ink border border-dgal-navy-2 rounded px-3 py-2"
          required
        />
        <input
          placeholder="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full bg-dgal-ink border border-dgal-navy-2 rounded px-3 py-2"
          required
        />
        <select
          value={plan}
          onChange={(e) => setPlan(e.target.value as 'founder' | 'standard')}
          className="w-full bg-dgal-ink border border-dgal-navy-2 rounded px-3 py-2"
        >
          <option value="standard">standard</option>
          <option value="founder">founder</option>
        </select>
        {err && <div className="text-sm text-red-400">{err}</div>}
        <button className="w-full bg-dgrp-red text-white font-semibold py-2 rounded hover:bg-dgrp-red-hover">
          Create
        </button>
      </form>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<'tenant_user' | 'tenant_admin' | 'global_superadmin'>('tenant_user');
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    const { data } = await api.get('/api/admin/users');
    setUsers(data.users);
  }
  useEffect(() => {
    load();
  }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      await api.post('/api/admin/users', { email, firstName, lastName, role });
      setEmail('');
      setFirstName('');
      setLastName('');
      load();
    } catch (e2: unknown) {
      const error = e2 as { response?: { data?: { error?: { message?: string } } } };
      setErr(error?.response?.data?.error?.message || 'Failed');
    }
  }

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div>
        <h2 className="text-lg font-semibold mb-3">Users</h2>
        <ul className="bg-dgal-navy/40 rounded border border-dgal-navy-2 divide-y divide-dgal-navy-2">
          {users.map((u) => (
            <li key={u.id} className="px-4 py-3">
              <div className="font-medium">
                {u.firstName} {u.lastName}
              </div>
              <div className="text-xs text-dgal-mist">
                {u.email} · {u.role} · {u.memberships?.length || 0} memberships
              </div>
            </li>
          ))}
        </ul>
      </div>

      <form onSubmit={create} className="bg-dgal-navy/40 rounded border border-dgal-navy-2 p-5 space-y-4 h-fit">
        <h2 className="text-lg font-semibold">Create user</h2>
        <input placeholder="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-dgal-ink border border-dgal-navy-2 rounded px-3 py-2" required />
        <div className="grid grid-cols-2 gap-2">
          <input placeholder="first" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full bg-dgal-ink border border-dgal-navy-2 rounded px-3 py-2" required />
          <input placeholder="last" value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full bg-dgal-ink border border-dgal-navy-2 rounded px-3 py-2" required />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value as typeof role)} className="w-full bg-dgal-ink border border-dgal-navy-2 rounded px-3 py-2">
          <option value="tenant_user">tenant_user</option>
          <option value="tenant_admin">tenant_admin</option>
          <option value="global_superadmin">global_superadmin</option>
        </select>
        {err && <div className="text-sm text-red-400">{err}</div>}
        <button className="w-full bg-dgrp-red text-white font-semibold py-2 rounded hover:bg-dgrp-red-hover">
          Create (default password: change-me-now)
        </button>
      </form>
    </div>
  );
}
